<template>
  <!-- customバリアントの場合はカスタムレイアウトでレンダリング -->
  <div v-if="variant === 'custom'" :class="customClasses" class="flex items-start">
    <!-- カスタムアイコン表示 -->
    <slot name="icon">
      <span
        class="material-symbols-outlined notranslate mr-2 mt-1"
        translate="no"
        aria-hidden="true"
        >{{ icon }}</span
      >
    </slot>
    <slot>{{ message }}</slot>
  </div>

  <!-- 標準バリアントの場合は定義されたスタイルを使用 -->
  <div v-else :class="['flex items-start rounded-md p-4 text-sm', variantClasses, customClasses]">
    <!-- カスタマイズ可能なアイコン表示、カスタムスロットがない場合はデフォルトアイコンを表示 -->
    <slot name="icon">
      <span
        class="material-symbols-outlined notranslate mr-2 mt-1"
        :class="iconClasses"
        translate="no"
        aria-hidden="true"
        >{{ computedIconName }}</span
      >
    </slot>

    <div :class="['leading-relaxed', textClasses, 'flex items-start']">
      <slot>{{ message }}</slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/**
 * メッセージ通知コンポーネント
 *
 * エラー、警告、情報などの通知メッセージを表示するための共通コンポーネント
 * カスタム以外のバリアントでは自動的に適切なアイコンが表示されます
 *
 * 基本的な使用例:
 * <MessageNotification variant="error" message="エラーが発生しました" />
 * <MessageNotification variant="warning" message="入力に問題があります" />
 * <MessageNotification variant="info" message="情報メッセージ" />
 * <MessageNotification variant="custom" icon="error" message="入力エラーがあります。修正をしてください。" custom-classes="text-red-500" />
 *
 * コンテンツをカスタマイズする場合:
 * <MessageNotification variant="warning">
 *   役割名は役職、等級を含めないものにしてください。詳細は「記入例」を参照してください。
 * </MessageNotification>
 *
 * アイコンをカスタマイズする場合（必要な場合のみ）:
 * <MessageNotification variant="warning">
 *   <template #icon>
 *     <span class="material-symbols-outlined notranslate mr-2 text-orange-400">warning</span>
 *   </template>
 *   カスタムメッセージ
 * </MessageNotification>
 */

type Variant = 'error' | 'warning' | 'info' | 'custom'

interface Props {
  /** メッセージのバリエーション (error, warning, info, custom) */
  variant?: Variant
  /** 表示するメッセージ */
  message?: string
  /** アイコン名（Material Symbolsのアイコン名） */
  icon?: string
  /** カスタムCSSクラス */
  customClasses?: string
  /** アイコンを塗りつぶすかどうか (Material Symbols FILL設定) */
  iconFillable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'info',
  message: '',
  icon: '',
  customClasses: '',
  iconFillable: true
})

// 各バリアントの設定を一つのオブジェクトに統合して管理
const variantConfig: Record<
  Variant,
  {
    icon: string
    iconClass: string
    bgClass: string
    textClass: string
  }
> = {
  error: {
    icon: 'error',
    iconClass: 'text-red-700',
    bgClass: 'bg-red-100',
    textClass: 'text-red-700'
  },
  warning: {
    icon: 'warning',
    iconClass: 'text-orange-600',
    bgClass: 'bg-yellow-50',
    textClass: 'text-yellow-600'
  },
  info: {
    icon: 'info',
    iconClass: 'text-blue-700',
    bgClass: 'bg-blue-100',
    textClass: 'text-blue-700'
  },
  custom: {
    icon: '',
    iconClass: '',
    bgClass: '',
    textClass: ''
  }
}

const config = computed(() => variantConfig[props.variant])
const computedIconName = computed(() => props.icon || config.value.icon)
const iconClasses = computed(() => config.value.iconClass)
const variantClasses = computed(() => config.value.bgClass)
const textClasses = computed(() => config.value.textClass)
const iconFillValue = computed(() => (props.iconFillable ? 1 : 0))
</script>

<style scoped>
.material-symbols-outlined {
  font-variation-settings: 'FILL' v-bind('iconFillValue');
  font-size: 1rem;
}
</style>
